import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";

import styles from "./SectionSidebar.module.scss";

export type SectionIconName =
  | "registrations"
  | "entries"
  | "waitlist"
  | "moveups"
  | "scoring"
  | "transactions"
  | "refunds"
  | "discounts"
  | "addons"
  | "incentives"
  | "calendar"
  | "settings"
  | "donations"
  | "dogs"
  | "paw"
  | "ticket"
  | "results"
  | "user"
  | "location"
  | "image"
  | "advanced"
  | "bell";

export interface ISectionNavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
}

export interface ISectionGroup {
  label: string;
  items: ISectionNavItem[];
}

export interface ISectionDefinition {
  name: string;
  group: string;
  icon: SectionIconName;
}

export interface ISectionHeader {
  title: string;
  subtitle?: string;
  imageUrl?: string;
  imageAlt?: string;
  fallbackName?: string;
  linkTo?: string;
  linkLabel?: string;
}

interface IProps {
  storageKey: string;
  menuLabel: string;
  eyebrow?: string;
  header: ISectionHeader;
  groups: ISectionGroup[];
  activeId?: string;
  onSelect(id: string): void;
  mobileOpen: boolean;
  onMobileClose(): void;
}

const iconProps = {
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

export const SectionIcon: React.FC<{ name: SectionIconName }> = ({ name }) => {
  switch (name) {
    case "registrations":
      return (
        <svg {...iconProps}>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case "entries":
      return (
        <svg {...iconProps}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      );
    case "waitlist":
      return (
        <svg {...iconProps}>
          <line x1="8" y1="6" x2="21" y2="6" />
          <line x1="8" y1="12" x2="21" y2="12" />
          <line x1="8" y1="18" x2="21" y2="18" />
          <line x1="3" y1="6" x2="3.01" y2="6" />
          <line x1="3" y1="12" x2="3.01" y2="12" />
          <line x1="3" y1="18" x2="3.01" y2="18" />
        </svg>
      );
    case "moveups":
      return (
        <svg {...iconProps}>
          <polyline points="17 11 21 7 17 3" />
          <line x1="21" y1="7" x2="9" y2="7" />
          <polyline points="7 13 3 17 7 21" />
          <line x1="15" y1="17" x2="3" y2="17" />
        </svg>
      );
    case "scoring":
      return (
        <svg {...iconProps}>
          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
          <rect x="8" y="2" width="8" height="4" rx="1" />
          <path d="M9 14l2 2 4-4" />
        </svg>
      );
    case "transactions":
      return (
        <svg {...iconProps}>
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
          <line x1="1" y1="10" x2="23" y2="10" />
        </svg>
      );
    case "refunds":
      return (
        <svg {...iconProps}>
          <polyline points="1 4 1 10 7 10" />
          <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
        </svg>
      );
    case "discounts":
      return (
        <svg {...iconProps}>
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
          <line x1="7" y1="7" x2="7.01" y2="7" />
        </svg>
      );
    case "addons":
      return (
        <svg {...iconProps}>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="16" />
          <line x1="8" y1="12" x2="16" y2="12" />
        </svg>
      );
    case "incentives":
      return (
        <svg {...iconProps}>
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      );
    case "calendar":
      return (
        <svg {...iconProps}>
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      );
    case "settings":
      return (
        <svg {...iconProps}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      );
    case "donations":
      return (
        <svg {...iconProps}>
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      );
    case "dogs":
      return (
        <svg {...iconProps}>
          <path d="M6 10V7.5A2.5 2.5 0 0 1 8.5 5h7A2.5 2.5 0 0 1 18 7.5V10" />
          <path d="M5 9c-1.7 0-3 1.3-3 3v1c0 1.7 1.3 3 3 3h1" />
          <path d="M19 9c1.7 0 3 1.3 3 3v1c0 1.7-1.3 3-3 3h-1" />
          <path d="M6 10v7a3 3 0 0 0 3 3h6a3 3 0 0 0 3-3v-7" />
          <circle cx="9" cy="13" r=".5" fill="currentColor" />
          <circle cx="15" cy="13" r=".5" fill="currentColor" />
          <path d="M10 16h4" />
        </svg>
      );
    case "paw":
      return (
        <svg {...iconProps}>
          <circle cx="11" cy="4" r="2" />
          <circle cx="18" cy="8" r="2" />
          <circle cx="20" cy="16" r="2" />
          <path d="M9 10a5 5 0 0 1 5 5v3.5a3.5 3.5 0 0 1-6.84 1.045Q6.52 17.48 4.46 16.84A3.5 3.5 0 0 1 5.5 10Z" />
        </svg>
      );
    case "ticket":
      return (
        <svg {...iconProps}>
          <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
          <path d="M13 5v2" />
          <path d="M13 17v2" />
          <path d="M13 11v2" />
        </svg>
      );
    case "results":
      return (
        <svg {...iconProps}>
          <path d="M8 21h8" />
          <path d="M12 17v4" />
          <path d="M7 4h10v5a5 5 0 0 1-10 0V4z" />
          <path d="M17 4h3a2 2 0 0 1 0 4h-3" />
          <path d="M7 4H4a2 2 0 0 0 0 4h3" />
        </svg>
      );
    case "user":
      return (
        <svg {...iconProps}>
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      );
    case "location":
      return (
        <svg {...iconProps}>
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      );
    case "image":
      return (
        <svg {...iconProps}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
      );
    case "advanced":
      return (
        <svg {...iconProps}>
          <line x1="4" y1="6" x2="20" y2="6" />
          <line x1="4" y1="12" x2="20" y2="12" />
          <line x1="4" y1="18" x2="20" y2="18" />
          <circle cx="8" cy="6" r="2" />
          <circle cx="16" cy="12" r="2" />
          <circle cx="10" cy="18" r="2" />
        </svg>
      );
    case "bell":
      return (
        <svg {...iconProps}>
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      );
  }
};

export const sectionsFromTabs = (tabs: { name: string }[], definitions: ISectionDefinition[]): ISectionGroup[] => {
  const groups: ISectionGroup[] = [];

  definitions.forEach((definition) => {
    const index = tabs.findIndex((tab) => tab.name === definition.name);
    if (index < 0) {
      return;
    }

    let group = groups.find((entry) => entry.label === definition.group);
    if (!group) {
      group = { label: definition.group, items: [] };
      groups.push(group);
    }

    group.items.push({
      id: String(index),
      label: definition.name,
      icon: <SectionIcon name={definition.icon} />,
    });
  });

  return groups;
};

const CollapseIcon: React.FC<{ collapsed: boolean }> = ({ collapsed }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={collapsed ? { transform: "scaleX(-1)" } : undefined}
  >
    <polyline points="11 17 6 12 11 7" />
    <polyline points="18 17 13 12 18 7" />
  </svg>
);

const initialsFor = (name?: string) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "ED";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const SectionNav: React.FC<{
  groups: ISectionGroup[];
  activeId?: string;
  collapsed: boolean;
  onSelect(id: string): void;
}> = ({ groups, activeId, collapsed, onSelect }) => {
  return (
    <nav className={styles.nav}>
      {groups.map((group) => (
        <div className={styles.group} key={group.label}>
          {!collapsed && <div className={styles.groupLabel}>{group.label}</div>}
          {group.items.map((item) => {
            const isActive = activeId === item.id;
            const badge = item.badge || 0;

            return (
              <button
                key={item.id}
                type="button"
                className={isActive ? `${styles.navItem} ${styles.active}` : styles.navItem}
                onClick={() => onSelect(item.id)}
                title={collapsed ? item.label : undefined}
                aria-current={isActive ? "page" : undefined}
              >
                {item.icon}
                {!collapsed && <span className={styles.navLabel}>{item.label}</span>}
                {badge > 0 && (
                  collapsed ? (
                    <span className={styles.badgeDot} />
                  ) : (
                    <span className={styles.badge}>{badge > 99 ? "99+" : badge}</span>
                  )
                )}
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  );
};

const HeaderBlock: React.FC<{ header: ISectionHeader; collapsed: boolean }> = ({ header, collapsed }) => {
  return (
    <div className={styles.club}>
      <div className={styles.avatar}>
        {header.imageUrl ? (
          <img src={header.imageUrl} alt={header.imageAlt || header.title} />
        ) : (
          initialsFor(header.fallbackName || header.title)
        )}
      </div>
      {!collapsed && (
        <>
          <div className={styles.clubName}>{header.title}</div>
          {header.subtitle ? <div className={styles.clubLocation}>{header.subtitle}</div> : null}
          {header.linkTo && header.linkLabel ? (
            <Link className={styles.viewEvent} to={header.linkTo}>{header.linkLabel}</Link>
          ) : null}
        </>
      )}
    </div>
  );
};

export const SectionMenuButton: React.FC<{ label: string; onClick(): void }> = ({ label, onClick }) => {
  return (
    <button type="button" className={styles.menuButton} onClick={onClick}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M4 6h16M4 12h16M4 18h10" />
      </svg>
      {label}
    </button>
  );
};

const SectionSidebar: React.FC<IProps> = ({
  storageKey,
  menuLabel,
  eyebrow,
  header,
  groups,
  activeId,
  onSelect,
  mobileOpen,
  onMobileClose,
}) => {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!mobileOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow || "unset";
    };
  }, [mobileOpen]);

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      const next = !current;
      try {
        localStorage.setItem(storageKey, next ? "1" : "0");
      } catch {
        // Ignore storage failures; the toggle still applies for this session.
      }
      return next;
    });
  };

  const handleSelect = (id: string) => {
    onSelect(id);
    if (mobileOpen) {
      onMobileClose();
    }
  };

  const drawer = mobileOpen ? ReactDOM.createPortal(
    <div className={styles.drawerBackdrop} onClick={onMobileClose}>
      <div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.drawerHeader}>
          <div>
            <div className={styles.drawerClub}>{eyebrow || header.title}</div>
            <div className={styles.drawerTitle}>{menuLabel}</div>
          </div>
          <button type="button" className={styles.drawerClose} onClick={onMobileClose} aria-label={`Close ${menuLabel.toLowerCase()}`}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <SectionNav groups={groups} activeId={activeId} collapsed={false} onSelect={handleSelect} />
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <aside className={collapsed ? `${styles.sidebar} ${styles.collapsed}` : styles.sidebar}>
        <HeaderBlock header={header} collapsed={collapsed} />
        <SectionNav groups={groups} activeId={activeId} collapsed={collapsed} onSelect={handleSelect} />
        <button
          type="button"
          className={styles.collapse}
          onClick={toggleCollapsed}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
        >
          <CollapseIcon collapsed={collapsed} />
          {!collapsed && <span>Collapse</span>}
        </button>
      </aside>
      {drawer}
    </>
  );
};

export default SectionSidebar;
